import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Container,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import ClientForm from '@/components/ClientForm';
import { ClientFormData } from '@/types';
import { ClientService } from '@/services/clientService';
import { useAuthStore } from '@/store/authStore';

const ClientCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (data: ClientFormData) => {
    if (!user) {
      setError('You must be logged in to create a client');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newClient = await ClientService.createClient(data, user.id);
      setSuccess(true);
      // Delay navigation slightly to show success message
      setTimeout(() => {
        navigate(`/clients/${newClient.id}`);
      }, 1500);
    } catch (err: any) {
      console.error('Error creating client:', err);
      setError(err.message || 'Failed to create client');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/clients');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
          <Link
            color="inherit"
            href="/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Dashboard
          </Link>
          <Link
            color="inherit"
            href="/clients"
            onClick={(e) => {
              e.preventDefault();
              navigate('/clients');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Clients
          </Link>
          <Typography color="text.primary">Create Client</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Create New Client
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Fill in the details below to add a new client to the system.
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        <ClientForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </Paper>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Client created successfully! Redirecting...
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClientCreatePage;

